import React from "react";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { colors, fontFamily } from "../LandingPage/tokens";
import { toAbsoluteUrl } from "../../_metronic/_helpers";

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: "100vh",
    background: colors.surfaceMuted,
    fontFamily
  },
  header: {
    background: colors.surface,
    borderBottom: `1px solid ${colors.border}`,
    padding: theme.spacing(2, 3)
  },
  headerInner: {
    maxWidth: 860,
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  logo: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    fontWeight: 700,
    fontSize: 18,
    color: colors.ink,
    textDecoration: "none"
  },
  logoMark: {
    height: 22,
    width: "auto",
    display: "block"
  },
  backLink: {
    fontSize: 13.5,
    fontWeight: 600,
    color: colors.inkMuted,
    textDecoration: "none",
    "&:hover": {
      color: colors.primary
    }
  },
  content: {
    maxWidth: 860,
    margin: "0 auto",
    padding: theme.spacing(6, 3, 10)
  },
  title: {
    fontSize: 32,
    fontWeight: 800,
    color: colors.ink,
    marginBottom: theme.spacing(1)
  },
  updated: {
    fontSize: 13.5,
    color: colors.inkFaint,
    marginBottom: theme.spacing(5)
  },
  section: {
    marginBottom: theme.spacing(4)
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: colors.ink,
    marginBottom: theme.spacing(1.5)
  },
  paragraph: {
    fontSize: 14.5,
    lineHeight: 1.75,
    color: colors.inkMuted,
    marginBottom: theme.spacing(1.5)
  },
  list: {
    margin: 0,
    paddingLeft: theme.spacing(3),
    "& li": {
      fontSize: 14.5,
      lineHeight: 1.75,
      color: colors.inkMuted,
      marginBottom: theme.spacing(1)
    }
  }
}));

const LAST_UPDATED = "September 10, 2026";

// Each section is plain data (not hardcoded JSX repetition) so the ToC and
// the body render from a single source of truth.
const SECTIONS = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    body: [
      "These Terms and Conditions (\"Terms\") govern your access to and use of the Anike platform, including any associated websites, mobile applications, and services (collectively, the \"Service\"), operated by Anike (\"Anike\", \"we\", \"us\", or \"our\").",
      "By creating an account, accessing, or using the Service, you agree to be bound by these Terms and by our Privacy Policy. If you are agreeing to these Terms on behalf of an organisation, you represent that you have the authority to bind that organisation, in which case \"you\" refers to that organisation.",
      "If you do not agree to these Terms, you must not access or use the Service."
    ]
  },
  {
    id: "eligibility",
    title: "2. Eligibility and Account Registration",
    body: [
      "You must be at least 18 years old and capable of forming a binding contract to use the Service. You are responsible for providing accurate, current, and complete information during registration and for keeping that information up to date.",
      "You are responsible for safeguarding your account credentials and for all activity that occurs under your account. You must notify us immediately of any unauthorised use of your account or any other breach of security.",
      "Accounts created for an organisation are subject to review and approval before full access to operational features is granted, as described during the onboarding process."
    ]
  },
  {
    id: "use-of-service",
    title: "3. Use of the Service",
    body: [
      "The Service is designed to help organisations manage assets, maintenance workflows, service requests, work orders, inventory, and related reporting. You may use the Service only for lawful business purposes and in accordance with these Terms."
    ],
    list: [
      "Do not use the Service to violate any applicable law or regulation.",
      "Do not attempt to gain unauthorised access to any part of the Service, other users' accounts, or any systems or networks connected to the Service.",
      "Do not upload or transmit any content that is unlawful, infringing, defamatory, or that contains malicious code.",
      "Do not interfere with or disrupt the integrity or performance of the Service.",
      "Do not reverse engineer, decompile, or attempt to extract the source code of the Service, except as permitted by law.",
      "Do not use automated means (bots, scrapers, etc.) to access the Service without our prior written consent."
    ]
  },
  {
    id: "organisations-roles",
    title: "4. Organisations, Roles, and Permissions",
    body: [
      "The Service allows an organisation's administrator to invite users, assign roles, and configure permissions that control what each user can view or do within that organisation's workspace. Organisation administrators are solely responsible for managing user access and for actions taken by users under their organisation.",
      "Data created within an organisation's workspace (assets, work orders, reports, and related records) is owned by that organisation, subject to Anike's rights to operate, maintain, and improve the Service as described in these Terms and our Privacy Policy."
    ]
  },
  {
    id: "content-data",
    title: "5. Your Content and Data",
    body: [
      "You retain all rights to the data, documents, and other content you submit, upload, or store through the Service (\"Your Content\"). You grant Anike a limited, non-exclusive licence to host, process, transmit, and display Your Content solely as necessary to provide and improve the Service.",
      "You are solely responsible for the accuracy, quality, and legality of Your Content and for having all rights necessary to submit it. We reserve the right to remove any content that violates these Terms or applicable law."
    ]
  },
  {
    id: "privacy",
    title: "6. Privacy and Data Protection",
    body: [
      "Our collection and use of personal information in connection with the Service is described in our Privacy Policy. By using the Service, you consent to the collection and use of information as described there.",
      "We implement reasonable technical and organisational measures designed to protect the confidentiality, integrity, and availability of data processed through the Service. No method of transmission or storage is completely secure, and we cannot guarantee absolute security."
    ]
  },
  {
    id: "fees",
    title: "7. Fees and Payment",
    body: [
      "Where the Service or any part of it is offered on a paid subscription basis, applicable fees, billing cycles, and payment terms will be communicated to you at the time of purchase or renewal. Fees are non-refundable except as required by law or as otherwise expressly stated.",
      "We may change our fees at any time, provided we give you reasonable advance notice before any change takes effect for your account."
    ]
  },
  {
    id: "intellectual-property",
    title: "8. Intellectual Property",
    body: [
      "The Service, including its software, design, graphics, and documentation, is owned by Anike and its licensors and is protected by intellectual property laws. Except for the limited rights expressly granted to you to use the Service, no other rights are granted, whether by implication, estoppel, or otherwise.",
      "\"Anike\" and associated logos are trademarks of Anike. You may not use them without our prior written permission."
    ]
  },
  {
    id: "third-party",
    title: "9. Third-Party Services",
    body: [
      "The Service may integrate with or link to third-party services (for example, email delivery, file storage, or payment processing). We are not responsible for the content, policies, or practices of any third-party service, and your use of such services is subject to their own terms."
    ]
  },
  {
    id: "availability",
    title: "10. Service Availability and Modifications",
    body: [
      "We aim to keep the Service available and reliable, but we do not guarantee uninterrupted or error-free operation. We may perform maintenance, updates, or modifications to the Service from time to time, with or without prior notice, and may suspend access where necessary for security, legal, or operational reasons."
    ]
  },
  {
    id: "warranty-disclaimer",
    title: "11. Disclaimer of Warranties",
    body: [
      "The Service is provided \"as is\" and \"as available\", without warranties of any kind, whether express, implied, or statutory, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will meet your specific requirements or that it will be uninterrupted, timely, secure, or error-free."
    ]
  },
  {
    id: "limitation-liability",
    title: "12. Limitation of Liability",
    body: [
      "To the maximum extent permitted by law, Anike and its officers, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenue, data, or business opportunities, arising out of or in connection with your use of, or inability to use, the Service.",
      "To the maximum extent permitted by law, our aggregate liability for any claim arising out of or relating to these Terms or the Service shall not exceed the amount you paid to us for the Service in the twelve (12) months preceding the event giving rise to the claim."
    ]
  },
  {
    id: "indemnification",
    title: "13. Indemnification",
    body: [
      "You agree to indemnify and hold Anike harmless from any claims, losses, liabilities, damages, and expenses (including reasonable legal fees) arising out of your use of the Service, Your Content, or your violation of these Terms or any applicable law."
    ]
  },
  {
    id: "termination",
    title: "14. Suspension and Termination",
    body: [
      "We may suspend or terminate your access to the Service if you violate these Terms, pose a security risk to the Service or other users, or if required by law. You may stop using the Service and close your account at any time.",
      "Upon termination, your right to use the Service will immediately cease. Provisions of these Terms that by their nature should survive termination (including ownership, disclaimers, indemnification, and limitations of liability) will survive."
    ]
  },
  {
    id: "changes",
    title: "15. Changes to These Terms",
    body: [
      "We may update these Terms from time to time to reflect changes in our practices, the Service, or applicable law. If we make material changes, we will provide reasonable notice, such as by posting a notice within the Service or updating the \"Last updated\" date below. Your continued use of the Service after changes take effect constitutes acceptance of the revised Terms."
    ]
  },
  {
    id: "governing-law",
    title: "16. Governing Law",
    body: [
      "These Terms are governed by the laws of the Federal Republic of Nigeria, without regard to its conflict of law principles. Any dispute arising out of or relating to these Terms or the Service shall be subject to the exclusive jurisdiction of the courts of Nigeria, unless otherwise required by applicable law."
    ]
  },
  {
    id: "contact",
    title: "17. Contact Us",
    body: [
      "If you have any questions about these Terms, please contact us through the contact details provided on our website's Contact page."
    ]
  }
];

export default function TermsAndConditions() {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <header className={classes.header}>
        <div className={classes.headerInner}>
          <Link to="/" className={classes.logo}>
            <img
              src={toAbsoluteUrl("/media/client-logos/logo.png")}
              alt=""
              className={classes.logoMark}
            />
            ANIKE
          </Link>
          <Link to="/auth/registration" className={classes.backLink}>
            &larr; Back to Sign Up
          </Link>
        </div>
      </header>

      <main className={classes.content}>
        <h1 className={classes.title}>Terms and Conditions</h1>
        <p className={classes.updated}>Last updated: {LAST_UPDATED}</p>

        {SECTIONS.map(section => (
          <section key={section.id} className={classes.section}>
            <h2 className={classes.sectionTitle}>{section.title}</h2>
            {section.body.map((paragraph, i) => (
              <p key={i} className={classes.paragraph}>
                {paragraph}
              </p>
            ))}
            {section.list && (
              <ul className={classes.list}>
                {section.list.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </main>
    </div>
  );
}
